<template>
  <form class="lead-manual-communication" @submit.prevent="submit">
    <header>
      <strong>{{ editing ? 'Edit communication' : 'Add communication' }}</strong>
      <AppButton
        v-if="editing"
        design="new"
        theme="dark"
        type="button"
        @click.native="cancelEdit"
      >
        Cancel edit
      </AppButton>
    </header>
    <div class="lead-manual-communication__grid">
      <label>
        <span>Communication method <em>*</em></span>
        <app-select
          v-model="form.method"
          design="new"
          :options="communicationMethods"
          :clearable="false"
          :error="Boolean(errors.method)"
          placeholder="Select method"
        />
        <small v-if="errors.method">{{ errors.method }}</small>
      </label>
      <div class="lead-manual-communication__direction">
        <span>Direction <em>*</em></span>
        <div
          class="lead-manual-communication__direction-options"
          :class="{ 'lead-manual-communication__error-control': errors.direction }"
          role="radiogroup"
          aria-label="Direction"
        >
          <button
            v-for="direction in directions"
            :key="direction.code"
            type="button"
            role="radio"
            :aria-checked="form.direction && form.direction.code === direction.code ? 'true' : 'false'"
            :class="[
              `lead-manual-communication__direction-option--${direction.code}`,
              { active: form.direction && form.direction.code === direction.code }
            ]"
            @click="form.direction = direction"
          >
            <span aria-hidden="true">{{ direction.code === 'inbound' ? '←' : '→' }}</span>
            {{ direction.name }}
          </button>
        </div>
        <small v-if="errors.direction">{{ errors.direction }}</small>
      </div>
      <label class="lead-manual-communication__note">
        <span>Note <i>optional</i></span>
        <AppTextarea
          v-model="form.report"
          design="new"
          rows="3"
          :error="Boolean(errors.report)"
          placeholder="Add notes about this communication..."
        />
        <small v-if="errors.report">{{ errors.report }}</small>
      </label>
    </div>
    <p v-if="formError" class="lead-manual-communication__form-error" role="alert">{{ formError }}</p>
    <AppButton
      design="new"
      class="lead-manual-communication__submit"
      type="submit"
      :disabled="saving || !canSubmit"
    >
      {{ saving ? 'Saving...' : (editing ? 'Save changes' : '+ Add communication') }}
    </AppButton>
  </form>
</template>

<script>
import {
  patchLeadFlowCommunication,
  postLeadFlowCommunication
} from '@/http'
import {
  createLeadContext,
  isActiveLeadContext
} from '@/views/crm/task-tracker/leadFlowContext'

const DIRECTIONS = [
  { code: 'inbound', name: 'Inbound' },
  { code: 'outbound', name: 'Outbound' }
]

export default {
  name: 'LeadFlowManualCommunicationForm',
  props: {
    leadId: {
      type: Number,
      required: true
    },
    communicationMethods: {
      type: Array,
      default: () => []
    },
    editing: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      directions: DIRECTIONS,
      form: {
        method: null,
        direction: null,
        report: ''
      },
      original: null,
      saving: false,
      errors: {},
      formError: '',
      contextGeneration: 0
    }
  },
  computed: {
    canSubmit() {
      if (!this.form.method || !this.form.direction) return false
      if (!this.editing) return true
      return Object.keys(this.patchPayload()).length > 0
    }
  },
  watch: {
    editing: {
      immediate: true,
      handler(editing) {
        if (editing) this.populate(editing)
        else this.reset()
      }
    },
    leadId() {
      this.contextGeneration += 1
      this.reset()
    }
  },
  methods: {
    async submit() {
      this.errors = {}
      this.formError = ''
      if (!this.form.method) this.$set(this.errors, 'method', 'Select a communication method.')
      if (!this.form.direction) this.$set(this.errors, 'direction', 'Select a direction.')
      if (Object.keys(this.errors).length || this.saving) return

      const payload = this.editing ? this.patchPayload() : this.createPayload()
      if (this.editing && !Object.keys(payload).length) return
      const context = createLeadContext(this.leadId, this.contextGeneration)
      if (!context.leadId) return
      this.saving = true
      this.$emit('pending-change', true)
      try {
        let response = null
        if (this.editing) {
          response = await patchLeadFlowCommunication(context.leadId, this.editing.id, payload)
        } else {
          response = await postLeadFlowCommunication(context.leadId, payload)
        }
        if (!isActiveLeadContext(context, this.leadId, this.contextGeneration)) return
        const item = response.data || {}
        const mode = this.editing ? 'edit' : 'create'
        this.$emit('saved', {
          mode,
          item,
          direction: this.form.direction && this.form.direction.code
        })
        this.$emit('cancel-edit')
        this.reset()
      } catch (error) {
        if (!isActiveLeadContext(context, this.leadId, this.contextGeneration)) return
        this.applyError(error)
      } finally {
        if (isActiveLeadContext(context, this.leadId, this.contextGeneration)) {
          this.saving = false
          this.$emit('pending-change', false)
        }
      }
    },
    createPayload() {
      const payload = {
        method_ids: [this.methodCode(this.form.method)],
        direction: this.form.direction.code
      }
      const report = this.form.report.trim()
      if (report) payload.report = report
      return payload
    },
    patchPayload() {
      if (!this.original) return {}
      const payload = {}
      const method = this.methodCode(this.form.method)
      const direction = this.form.direction && this.form.direction.code
      const report = this.form.report.trim()
      if (method !== this.original.method) payload.method_ids = [method]
      if (direction !== this.original.direction) payload.direction = direction
      if (report !== this.original.report) payload.report = report
      return payload
    },
    populate(communication) {
      const methodCode = this.methodCode((communication.methods || [])[0])
      const directionCode = communication.direction && communication.direction.code
      const method = this.communicationMethods.find(item => this.methodCode(item) === methodCode)
        || (communication.methods || [])[0]
        || null
      const direction = DIRECTIONS.find(item => item.code === directionCode) || null
      const report = communication.report || ''
      this.form = {
        method,
        direction,
        report
      }
      this.original = {
        method: methodCode,
        direction: directionCode || null,
        report: report.trim()
      }
      this.errors = {}
      this.formError = ''
    },
    applyError(error) {
      const data = error.response && error.response.data
      const message = data && data.message ? data.message : 'Unable to save this communication.'
      const field = {
        method: 'method',
        method_ids: 'method',
        communication_method: 'method',
        direction: 'direction',
        report: 'report'
      }[data && data.field]
      if (field) this.errors = { [field]: message }
      else this.formError = message
    },
    methodCode(method) {
      return method ? (method.code || method.id || null) : null
    },
    cancelEdit() {
      if (this.saving) return
      this.$emit('cancel-edit')
      this.reset()
    },
    reset() {
      this.form = {
        method: null,
        direction: null,
        report: ''
      }
      this.original = null
      this.errors = {}
      this.formError = ''
    }
  }
}
</script>

<style scoped>
.lead-manual-communication {
  display: grid;
  gap: 12px;
  padding-top: 14px;
  border-top: 1px solid #e3e7e1;
}

.lead-manual-communication > header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.lead-manual-communication > header strong {
  color: #526264;
  font-size: 10px;
  text-transform: uppercase;
}

.lead-manual-communication > header button {
  padding: 0;
  border: 0;
  background: transparent;
  color: #74817f;
  font-size: 9px;
  cursor: pointer;
}

.lead-manual-communication__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.lead-manual-communication label {
  display: grid;
  min-width: 0;
  gap: 5px;
  margin: 0;
}

.lead-manual-communication__direction {
  display: grid;
  min-width: 0;
  gap: 5px;
}

.lead-manual-communication label > span,
.lead-manual-communication__direction > span {
  color: #667675;
  font-size: 9px;
  font-weight: 700;
}

.lead-manual-communication label i {
  color: #929c9a;
  font-style: normal;
  font-weight: 500;
}

.lead-manual-communication label em,
.lead-manual-communication__direction em {
  color: #ba554d;
  font-style: normal;
}

.lead-manual-communication label small,
.lead-manual-communication__direction small {
  color: #b85049;
  font-size: 9px;
}

.lead-manual-communication__direction-options {
  position: relative;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px;
  min-height: 40px;
  padding: 3px;
  border: 1px solid #d9dfd8;
  border-radius: 6px;
  background: #fff;
}

.lead-manual-communication__direction-options::after {
  position: absolute;
  top: 9px;
  bottom: 9px;
  left: 50%;
  width: 1px;
  background: #d9dfd8;
  content: '';
  pointer-events: none;
}

.lead-manual-communication__direction-options button {
  position: relative;
  z-index: 1;
  display: inline-flex;
  min-width: 0;
  min-height: 32px;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 10px;
  border: 1px solid transparent;
  border-radius: 4px;
  background: transparent;
  color: #667675;
  font-size: 10px;
  font-weight: 700;
  cursor: pointer;
}

.lead-manual-communication__direction-option--inbound.active {
  border-color: #8fcdb7;
  background: #eaf8f2;
  color: #1b7959;
}

.lead-manual-communication__direction-option--outbound.active {
  border-color: #9abdef;
  background: #edf4ff;
  color: #1769e0;
}

.lead-manual-communication__note {
  grid-column: 1 / -1;
}

.lead-manual-communication textarea {
  width: 100%;
  min-height: 67px;
  resize: vertical;
  padding: 9px 10px;
  border: 1px solid #d9dfd8;
  border-radius: 6px;
  color: #26393b;
  font: inherit;
  font-size: 10px;
  line-height: 15px;
}

.lead-manual-communication__error-control {
  border-color: #c86b63 !important;
}

.lead-manual-communication__form-error {
  margin: 0;
  color: #b85049;
  font-size: 9px;
}

.lead-manual-communication__submit {
  min-height: 34px;
  justify-self: start;
  padding: 0 13px;
  border: 1px solid #77a63a;
  border-radius: 5px;
  background: #77b017;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  cursor: pointer;
}

.lead-manual-communication__submit:disabled {
  cursor: default;
  opacity: .55;
}

@media (max-width: 600px) {
  .lead-manual-communication__grid {
    grid-template-columns: 1fr;
  }

  .lead-manual-communication__note {
    grid-column: auto;
  }
}
</style>
